import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_debounced_function } from '@/hooks/debounce/use_debounced_function';
import { use_component_is_mounted_0 } from '@/hooks/is_mounted/use_component_is_mounted_0';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { assert_type_string } from '@shared/asserts/utils_asserts';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__base_string } from '@shared/dto/generation/graph_invoke/_fields/_primitives/string/FIELD_PRIMITIVE__base_string';
import { useRef, useState } from 'react';
import { G_UI_FIELD_PRIMITIVE__string_list_item } from './G_UI_FIELD_PRIMITIVE__string_list_item';

interface PROPS {
  field: FIELD_PRIMITIVE__base_string;
}

export function G_UI_FIELD_PRIMITIVE__string_list({ field }: PROPS) {
  //key - field.value
  const ref_items = useRef<Map<string, HTMLDivElement>>(new Map());

  const { g_hovered_grid2d_column, g_hovered_grid2d_row, g_hovered_grid2d_dimension_from_grid } = use_slice_ui([
    'g_hovered_grid2d_column',
    'g_hovered_grid2d_row',
    'g_hovered_grid2d_dimension_from_grid',
  ]);

  const { is_mounted } = use_component_is_mounted_0();

  const [is_scrolled_after_mount, set_is_scrolled_after_mount] = useState(false);

  const scroll_list_debounced_row = use_debounced_function(scroll_list_to, CONSTANTS_TIME__client.DEBOUNCE_GENERATION_GRID2D_AND_LIST);
  const scroll_list_debounced_column = use_debounced_function(scroll_list_to, CONSTANTS_TIME__client.DEBOUNCE_GENERATION_GRID2D_AND_LIST);

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (is_scrolled_after_mount) return;

    let is_scrolled = scroll_list_to('instant', field);

    set_is_scrolled_after_mount(is_scrolled);
  }, [is_mounted]);

  use_effect(() => {
    if (!g_hovered_grid2d_dimension_from_grid) return;

    scroll_list_debounced_column('smooth', g_hovered_grid2d_column);
    scroll_list_debounced_row('smooth', g_hovered_grid2d_row);
  }, [g_hovered_grid2d_column, g_hovered_grid2d_row]);

  /*
   * EVENTS
   */

  /*
   * METHODS
   */
  function scroll_list_to(behavior: 'smooth' | 'instant', target_field: FIELD__base) {
    if (!(target_field instanceof FIELD_PRIMITIVE__base_string)) return false;
    if (target_field.meta.field_id !== field.meta.field_id) return false;
    if (!is_mounted) return false;

    let value_hovered = target_field.get_value();

    assert_type_string(value_hovered);

    const ref = ref_items.current.get_or_assert(value_hovered);

    ref.scrollIntoView({ behavior, block: 'center' });

    return true;
  }

  /*
   * RENDERS
   */
  function render_items() {
    let out: JSX.Element[] = [];

    for (let item_value_range of field.get_values_allowed()) {
      let field_value_range = field.clone({ value: item_value_range });

      let view = (
        <G_UI_FIELD_PRIMITIVE__string_list_item
          ref={element => element && ref_items.current.set(item_value_range, element)}
          key={`${field.meta.field_id}_${item_value_range}`}
          field={field_value_range}
        />
      );

      out.push(view);
    }

    return out;
  }

  /*
   * HTML
   */
  return <div className="flex flex-col w-full min-w-0 h-full overflow-y-auto">{render_items()}</div>;
}
